<template>
  <div>
    <v-dialog
      lazy
      scrollable
      :persistent="hasOverlayOptions"
      :value="show"
      :width="800"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @input="(val) => $emit('update:show', val)"
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout column>
            <v-layout align-center>
              <div class="headline">
                {{ dialogTitle }}
              </div>
              <v-btn
                class="ml-auto mr-0 my-0"
                :disabled="loading"
                flat
                icon
                data-cy="edit-overlay-data-dialog-abort-btn"
                :title="$t('close')"
                @click="abort()"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider class="mt-2" />
        <v-card-text class="pt-1">
          <div class="subheading mt-2">
            {{ $t('customizeOverlayDataHint') + ': ' }}
            <strong>{{ spot.title }}</strong>
          </div>
          <!-- Branding Dropdown -->
          <v-layout v-if="showBrandingsDropdown">
            <tv-template-branding-widget
              ref="brandingwidget"
              class="ml-auto"
              :title="lastBrandingApplied ? lastBrandingApplied.name : null"
              :overlay-options="firstEntry.spot.overlay_options"
              :brandings="brandings"
              :active="lastBrandingApplied !== null"
              @applied="onBrandingApplied"
            />
          </v-layout>
          <v-layout
            justify-center
            class="mt-2 mb-2"
          >
            <tv-template-preview
              v-if="isEditableSpotWithHtmlTemplate && !!tvformat"
              ref="preview"
              :key="show"
              :html-overlay="editableTemplate.html_overlay"
              :background-image="
                !editableTemplate.use_tvspot_video_for_background &&
                  editableTemplate.use_tvspot_poster_for_preview_background
                  ? editableTemplate.video_poster_for_editing
                  : null
              "
              :background-video="
                editableTemplate.use_tvspot_video_for_background
                  ? spot.preview_background_url
                  : null
              "
              :width="tvformat.width"
              :height="tvformat.height"
              :max-height="320"
              :is-multiscreen="tvformat.is_multiscreen_format"
              :duration="spot.duration"
              :fake-player-progress="editableTemplate.is_animated"
              :fake-fade-in-fade-out="editableTemplate.fade_enabled"
              :fade-in-start="editableTemplate.fadein_start"
              :fade-in-stop="editableTemplate.fadein_stop"
              :fade-out-start="editableTemplate.fadeout_start"
              :fade-out-stop="editableTemplate.fadeout_stop"
              :class="{'elevation-3': editableTemplate.is_animated}"
              :onload-callback="populateIframeWithInitialOverlayData"
              show-zoom-buttons
            />
            <v-img
              v-else-if="spot.poster"
              contain
              :max-height="$vuetify.breakpoint.xsOnly ? 96 : 144"
              :src="posterURL"
            />
          </v-layout>
          <v-layout justify-center>
            <v-progress-circular
              v-if="!hasOverlayOptions"
              indeterminate
              color="primary"
              class="mb-2"
            />
            <overlay-data
              v-else-if="!!firstEntry"
              ref="overlaydata"
              :key="show"
              class="grow-1"
              validate-on-start
              hide-flagged-groups-during-activation
              :overlay-options="overlayOptions"
              :overlay-data="firstEntry.overlay_data"
              @changed="refreshIframeWithOverlayData()"
              @change-image="onImageChangeRequested"
              @valid="onOverlayDataValidityChanged"
            />
          </v-layout>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <btn-ok
            v-if="hasOverlayOptions"
            data-cy="edit-overlay-data-dialog-confirm-btn"
            round
            :disabled="!isOverlayDataValid"
            :loading="loading"
            :title="$t('confirm')"
            :label="confirmButtonLabel"
            @click="confirm()"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- TVTemplate Image Replacement -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onImageChangeConfirmed"
    />
  </div>
</template>

<script>
import {deepCopyObject} from '@/utils';

import {onEditableTVSpotTemplateImageReplacementCropped} from '@/mixins/imageReplacement';
import overlayOptions from '@/mixins/overlayOptions.js';

import BtnOk from '@/components/ButtonOk';
import ImageReplacement from '@/components/ImageReplacement';
import OverlayData from '@/components/OverlayData';
import TvTemplateBrandingWidget from '@/components/TvTemplateBrandingWidget.vue';
import TvTemplatePreview from '@/components/TvTemplatePreview';

export default {
  components: {
    BtnOk,
    ImageReplacement,
    OverlayData,
    TvTemplateBrandingWidget,
    TvTemplatePreview,
  },
  mixins: [overlayOptions],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    /**
     * A list of serialized TVSpotEntry objects.
     */
    entries: {
      type: Array[Object],
      required: true,
    },
    numEntriesOverall: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      isOverlayDataValid: false,
    };
  },
  computed: {
    dialogTitle() {
      if (this.entries.length === 1) {
        const entry = this.entries[0];
        // Adapt title when viewing from content-perspective.
        if (entry.decoder && entry.decoder.store) {
          return this.$t('updateOverlayDataOnPlayer', {
            player: `${entry.decoder.store.city}, ${entry.decoder.store.name}: ${entry.decoder.description}`,
          });
        }
      }
      return this.confirmButtonLabel;
    },
    confirmButtonLabel() {
      if (!!this.numEntriesOverall) {
        return this.$t('updateOverlayDataOnNumPlayers', {
          num: `${this.entries.length}/${this.numEntriesOverall}`,
        });
      }
      return this.$t('customize');
    },
    brandings() {
      return this.$store.state.common.brandings;
    },
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    showBrandingsDropdown() {
      const editableHtmlSpotsFromTemplate = this.entries.filter((entry) => {
        return (
          entry.ctype == 'tv' &&
          !!entry.spot &&
          !!entry.spot.editable_template &&
          entry.spot.editable_template.is_brandable
        );
      });
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        editableHtmlSpotsFromTemplate.length > 0
      );
    },
    /**
     * We assume all entries passed here to use the same TVSpot,
     * so we can use any to read spot information from.
     */
    firstEntry() {
      return this.entries[0];
    },
    /**
     * To make this work from SpotDetail, SpotEntry and SpotDecoderEntry
     * components, we can get the TVSpotBundle ID from either the
     * current route or from the (first) entry object itself.
     */
    bundleId() {
      let bundleId = this.$route.params.spotId;
      if (!bundleId) {
        bundleId = this.firstEntry.bundle_id;
      }
      return bundleId;
    },
    spot() {
      return this.firstEntry ? this.firstEntry.spot : {};
    },
    decoder() {
      return this.firstEntry ? this.firstEntry.decoder : {};
    },
    tvformat() {
      if (this.decoder && Object.keys(this.decoder).length) {
        return this.decoder.tvformat;
      } else if (this.spot && Object.keys(this.spot).length) {
        return this.spot.tvformat;
      }
      return null;
    },
    posterURL() {
      return this.spot.poster_url;
    },
    isEditableSpotWithHtmlTemplate() {
      return this.editableTemplate !== null;
    },
  },
  watch: {
    /**
     * Fetch/reset overlayOptions when dialog is shown/closed.
     */
    show: function (show) {
      if (show) {
        this.$store
          .dispatch('MAYBE_LIST_BRANDINGS')
          .then(() => this.initializeBranding());
      }

      if (show && !this.overlayOptions) {
        this.fetchOverlayInformationForTVSpotBundleId(this.bundleId);
      } else {
        this.resetOverlayOptions();
      }
    },
  },
  methods: {
    initializeBranding() {
      if (
        this.firstEntry &&
        this.firstEntry.overlay_data &&
        this.firstEntry.overlay_data.branding
      ) {
        // Fallback needed in case we currently have no own Branding,
        // but the HTML requires one.
        const fallbackBranding = this.firstEntry.overlay_data.branding;

        const matchingBranding =
          this.brandings.filter(
            (branding) =>
              branding.id == this.firstEntry.overlay_data.branding.id
          )[0] || fallbackBranding;

        if (matchingBranding) {
          this.lastBrandingApplied = matchingBranding;
        }
      }
    },
    onBrandingApplied(context) {
      this.lastBrandingApplied = context.branding;
      this.refreshIframeWithOverlayData();
    },
    injectLastBrandingAppliedIntoOverlayData(overlayData) {
      if (!overlayData.branding) {
        overlayData.branding = {};
      }
      if (this.lastBrandingApplied) {
        overlayData.branding = this.lastBrandingApplied;
      }
    },
    injectTvSpotDurationIntoOverlayData(overlayData) {
      if (!this.isEditableSpotWithHtmlTemplate) {
        return;
      }
      overlayData.duration = this.spot.duration;
    },
    populateIframeWithInitialOverlayData() {
      const overlayData = deepCopyObject(this.firstEntry.overlay_data);
      const previewWidget = this.$refs.preview;
      previewWidget.updateOverlayData(overlayData);
    },
    refreshIframeWithOverlayData() {
      const overlayDataWidget = this.$refs.overlaydata;
      if (!overlayDataWidget) {
        return;
      }

      const overlayData = overlayDataWidget.getOverlayData();
      this.injectTvSpotDurationIntoOverlayData(overlayData);
      this.injectLastBrandingAppliedIntoOverlayData(overlayData);

      const previewWidget = this.$refs.preview;
      if (previewWidget) {
        previewWidget.updateOverlayData(overlayData);
      }
    },
    /**
     * Update form validation flag when form widget reports changes.
     */
    onOverlayDataValidityChanged(allFieldsAreValid) {
      this.isOverlayDataValid = allFieldsAreValid;
    },
    /**
     * Delegate request to image replacement handler.
     */
    onImageChangeRequested(opts) {
      this.$refs.imagereplacement.onImageChangeRequested(opts);
    },
    onImageChangeConfirmed(payload) {
      let firstOverlayOptions = null;
      if (this.spot) {
        firstOverlayOptions = this.spot.overlay_options;
      }
      onEditableTVSpotTemplateImageReplacementCropped(
        this.editableTemplate.id,
        firstOverlayOptions,
        payload.fieldName,
        payload.file,
        payload.originalFilename
      );
    },
    updateEntryOverlayData(entries) {
      const overlayData = this.$refs.overlaydata.getOverlayData();
      this.injectTvSpotDurationIntoOverlayData(overlayData);
      this.injectLastBrandingAppliedIntoOverlayData(overlayData);

      this.loading = true;
      const promises = [];
      for (let entry of entries) {
        const promise = this.updateOverlayDataForEntryIdent(
          entry.ident,
          overlayData
        );
        promises.push(promise);

        // Update entry in frontend for immediate feedback.
        entry.overlay_data = overlayData;
      }
      Promise.all(promises)
        .then((responses) => {
          // Update entry status in frontend e.g. v-chip.
          for (let entry of entries) {
            if (this.isEditableSpotWithHtmlTemplate) {
              entry.processed = false;
            }
          }

          this.$bus.$emit('alert-success', {
            message: 'genericEntryUpdateSuccess',
            translate: true,
          });

          this.$emit('submitted');
          this.closeDialog();
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 403) {
            this.$bus.$emit('alert-error', {
              message: 'noPermission',
              translate: true,
              timeout: 3000,
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeDialog() {
      this.$emit('update:show', false);
    },
    abort() {
      this.closeDialog();
    },
    confirm() {
      this.updateEntryOverlayData(this.entries);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/EditOverlayDataDialog.vue